import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const result = await login(email, password, remember);
        
        if (result.success) {
            toast.success(t('auth.welcome_back'));
            // If we were redirected here from a guest-checkout flow, return to that page
            const next = params.get('next');
            navigate(next && next.startsWith('/') ? next : '/dashboard');
        } else {
            toast.error(result.error);
        }
        
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0] flex" data-testid="login-page">
            {/* Left side - Image */}
            <div className="hidden lg:block lg:w-1/2 relative">
                <img
                    src="https://static.prod-images.emergentagent.com/jobs/bf9e597f-643a-4721-b953-4863e98c43ff/images/7a3cd803f6df4f4ff7f1070d57e90d619fe04cc0c40252098c07e9452f85f74c.png"
                    alt="Library"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#5B2D8E]/20"></div>
                <div className="absolute bottom-12 left-12 right-12 text-white">
                    <h2 className="font-serif text-4xl mb-4">{t('auth.sign_in_heading')}</h2>
                    <p className="font-sans text-lg text-white/80">{t('auth.sign_in_sub')}</p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <Link to="/" className="block mb-12" data-testid="login-logo">
                        <h1 className="font-serif text-2xl text-[#1A1A2E]">Methode 4 Life</h1>
                    </Link>

                    <div className="mb-8">
                        <h2 className="font-serif text-3xl text-[#1A1A2E] mb-2">{t('nav.sign_in')}</h2>
                        <p className="font-sans text-[#4A4A68]">
                            {t('auth.no_account')}{' '}
                            <Link to="/register" className="text-[#5B2D8E] hover:underline" data-testid="login-register-link">
                                {t('auth.register_cta')}
                            </Link>
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                        <div>
                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">
                                {t('auth.email')}
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full"
                                placeholder="you@example.com"
                                required
                                data-testid="login-email"
                            />
                        </div>

                        <div>
                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">
                                {t('auth.password')}
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pr-10"
                                    placeholder="••••••••"
                                    required
                                    data-testid="login-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A4A68] hover:text-[#5B2D8E]"
                                    data-testid="login-toggle-password"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={remember}
                                    onChange={(e) => setRemember(e.target.checked)}
                                    className="w-4 h-4 accent-[#5B2D8E]"
                                    data-testid="remember-me"
                                />
                                <span className="font-sans text-sm text-[#4A4A68]">{t('auth.remember_me')}</span>
                            </label>
                            <Link to="/forgot-password" className="font-sans text-sm text-[#5B2D8E] hover:underline">
                                {t('auth.forgot_password')}
                            </Link>
                        </div>

                        <button
                            type="submit"
                            className="btn-primary w-full"
                            disabled={loading}
                            data-testid="login-submit"
                        >
                            {loading ? t('auth.signing_in') : t('auth.sign_in_cta')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
